import {
  makeStyles,
  tokens,
  Text,
  Button,
  Card,
  CardHeader,
  Badge,
  Spinner,
  Divider,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  OverlayDrawer,
} from '@fluentui/react-components';
import {
  Alert24Regular,
  Checkmark24Regular,
  Delete24Regular,
  Info24Regular,
  Warning24Regular,
  ErrorCircle24Regular,
  Dismiss24Regular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  useGetApiV1NotificationsUserId,
  useDeleteApiV1NotificationsNotificationId,
  deleteApiV1NotificationsNotificationId,
} from '@api/generated/notifications/notifications/notifications';
import { useAuth } from '@app/providers/AuthProvider';
import { useCallback, useEffect } from 'react';

const useStyles = makeStyles({
  notificationList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  notificationCard: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  notificationContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  notificationMeta: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    marginTop: tokens.spacingVerticalXS,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: tokens.spacingVerticalXXL,
    gap: tokens.spacingVerticalM,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
});

// Backend enum values (UserNotificationTypeDto) - API returns numeric values
const NOTIFICATION_TYPE_NAMES = [
  'General',
  'TokenCreated',
  'TokenCreationFailed',
  'AppointmentCreated',
  'TeamAppointmentCreated',
  'VisitorCheckedIn',
  'VisitorCheckedOut',
  'ApprovalRequired',
  'WorkflowCompleted',
  'WorkflowFailed',
] as const;

type NotificationType = (typeof NOTIFICATION_TYPE_NAMES)[number];

// API may return type as number (enum) or string — normalize to string name
function resolveTypeName(type: number | string): NotificationType {
  if (typeof type === 'number') {
    return NOTIFICATION_TYPE_NAMES[type] ?? 'General';
  }
  return (type as NotificationType) || 'General';
}

interface UserNotificationDto {
  id: string;
  userId: string;
  type: number | string; // Backend sends enum as number
  createdAt: string;
  referenceId: string;
  messageData: Record<string, string>;
}

interface NotificationPanelProps {
  open: boolean;
  onClose: () => void;
}

export function NotificationPanel({ open, onClose }: NotificationPanelProps) {
  const styles = useStyles();
  const { t } = useTranslation('notifications');
  const queryClient = useQueryClient();
  const { user, hasAnyRole } = useAuth();
  const navigate = useNavigate();

  const userId = user?.userId || '';

  // Request browser notification permission when the user opens the panel (user gesture)
  useEffect(() => {
    if (open && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, [open]);

  const { data: notifications, isLoading } = useGetApiV1NotificationsUserId<UserNotificationDto[]>(
    userId,
    {
      query: {
        enabled: !!userId && open,
      },
    }
  );

  // Invalidate all notification queries (panel + header badge)
  const invalidateNotifications = () => {
    queryClient.invalidateQueries({
      predicate: (query) =>
        typeof query.queryKey[0] === 'string' &&
        query.queryKey[0].includes('/api/v1/notifications/'),
    });
  };

  const deleteMutation = useDeleteApiV1NotificationsNotificationId({
    mutation: {
      onSuccess: invalidateNotifications,
    },
  });

  const deleteAllMutation = useMutation({
    mutationFn: async () => {
      if (notifications) {
        for (const notification of notifications) {
          await deleteApiV1NotificationsNotificationId(notification.id);
        }
      }
    },
    onSuccess: invalidateNotifications,
  });

  const getTypeIcon = (type: NotificationType) => {
    switch (type) {
      case 'AppointmentCreated':
      case 'TeamAppointmentCreated':
      case 'TokenCreated':
      case 'VisitorCheckedIn':
      case 'VisitorCheckedOut':
      case 'WorkflowCompleted':
        return <Checkmark24Regular style={{ color: tokens.colorPaletteGreenForeground1 }} />;
      case 'TokenCreationFailed':
      case 'WorkflowFailed':
        return <ErrorCircle24Regular style={{ color: tokens.colorPaletteRedForeground1 }} />;
      case 'ApprovalRequired':
        return <Warning24Regular style={{ color: tokens.colorPaletteYellowForeground1 }} />;
      case 'General':
      default:
        return <Info24Regular style={{ color: tokens.colorBrandForeground1 }} />;
    }
  };

  const notificationCount = notifications?.length || 0;
  const isGateKeeperOrAdmin = hasAnyRole(['gate_keeper', 'admin']);

  const handleNotificationClick = useCallback((notification: UserNotificationDto) => {
    if (!notification.referenceId || notification.referenceId === '00000000-0000-0000-0000-000000000000') {
      return;
    }

    onClose();
    const typeName = resolveTypeName(notification.type);

    switch (typeName) {
      case 'AppointmentCreated':
      case 'TeamAppointmentCreated':
      case 'VisitorCheckedIn':
      case 'VisitorCheckedOut':
        if (isGateKeeperOrAdmin) {
          navigate(`/reception/${notification.referenceId}`);
        } else {
          navigate('/dashboard');
        }
        break;
      case 'ApprovalRequired':
      case 'WorkflowCompleted':
      case 'WorkflowFailed':
        navigate('/reception');
        break;
      default:
        break;
    }
  }, [isGateKeeperOrAdmin, navigate, onClose]);

  const createMessage = (notification: UserNotificationDto): string => {
    const typeName = resolveTypeName(notification.type);
    let message = t(`types.${typeName}Message`, '');
    if (notification.messageData) {
      // Normalize legacy keys: old notifications used "UserName", new ones use "VisitorName"
      const data = { ...notification.messageData };
      if (data['UserName'] && !data['VisitorName']) {
        data['VisitorName'] = data['UserName'];
      }
      Object.entries(data).forEach(([key, value]) => {
        message = message.replace(`{${key}}`, value);
      });
    }
    return message;
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('timeAgo.justNow');
    if (diffMins < 60) return t('timeAgo.minutes', { count: diffMins });
    if (diffHours < 24) return t('timeAgo.hours', { count: diffHours });
    if (diffDays < 7) return t('timeAgo.days', { count: diffDays });
    return date.toLocaleDateString();
  };

  return (
    <OverlayDrawer
      open={open}
      onOpenChange={(_, data) => { if (!data.open) onClose(); }}
      position="end"
      size="medium"
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              icon={<Dismiss24Regular />}
              onClick={onClose}
              aria-label={t('details.close')}
            />
          }
        >
          <div className={styles.headerActions}>
            <Alert24Regular />
            <span>{t('title')}</span>
            {notificationCount > 0 && (
              <Badge appearance="filled" color="danger">{notificationCount}</Badge>
            )}
          </div>
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody>
        {notificationCount > 0 && (
          <>
            <Button
              appearance="subtle"
              icon={<Delete24Regular />}
              onClick={() => deleteAllMutation.mutate()}
              disabled={deleteAllMutation.isPending}
              size="small"
              style={{ alignSelf: 'flex-end', marginBottom: tokens.spacingVerticalS }}
            >
              {t('deleteAll')}
            </Button>
            <Divider style={{ marginBottom: tokens.spacingVerticalS }} />
          </>
        )}

        {isLoading ? (
          <div className={styles.emptyState}>
            <Spinner size="large" />
            <Text>{t('loading')}</Text>
          </div>
        ) : !notifications || notifications.length === 0 ? (
          <div className={styles.emptyState}>
            <Alert24Regular style={{ fontSize: 48, opacity: 0.5 }} />
            <Text>{t('noNotifications')}</Text>
          </div>
        ) : (
          <div className={styles.notificationList}>
            {notifications.map((notification) => {
              const typeName = resolveTypeName(notification.type);
              return (
              <Card
                key={notification.id}
                className={styles.notificationCard}
                onClick={() => handleNotificationClick(notification)}
                size="small"
              >
                <CardHeader
                  image={getTypeIcon(typeName)}
                  header={
                    <div className={styles.notificationContent}>
                      <div>
                        <Text size={300} weight="semibold">
                          {t(`types.${typeName}`)}
                        </Text>
                        <Text size={200} style={{ display: 'block', marginTop: tokens.spacingVerticalXS }}>
                          {createMessage(notification)}
                        </Text>
                        <div className={styles.notificationMeta}>
                          <span>{formatTimeAgo(notification.createdAt)}</span>
                        </div>
                      </div>
                      <Button
                        appearance="subtle"
                        icon={<Delete24Regular />}
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteMutation.mutate({ notificationId: notification.id });
                        }}
                      />
                    </div>
                  }
                />
              </Card>
              );
            })}
          </div>
        )}
      </DrawerBody>
    </OverlayDrawer>
  );
}
